/**
 * adminCache — one consistent caching layer for the admin dashboard, built on the
 * existing in-memory `cacheService` (LRU + TTL, per SPA session, cleared on hard
 * refresh). Use `cachedRead` for expensive one-shot reads (whole-collection
 * getDocs / loadRevenueData / getCountFromServer). NEVER wrap an onSnapshot live
 * subscription — those are already live.
 *
 * Volatility buckets (pick the TTL that matches how often the data changes):
 *   REFERENCE  — rarely changes (games/schedule, players, sponsors, positions)
 *   BUSINESS   — orders/revenue, marketing contacts, rollup counts
 *   VOLATILE   — changes on this admin's own edits (short, and invalidate on write)
 *
 * When a page WRITES to a collection it also reads, call `invalidate(key)` (or
 * `invalidatePrefix`) right after the successful write so the admin's own change
 * shows immediately instead of waiting for the TTL.
 */
import { cacheService } from './cache-service';

export const CACHE_TTL = {
  STATIC: 24 * 60 * 60 * 1000, // 24 h — near-immutable data guarded by a version
                               // sentinel (e.g. games); only re-fetched when the
                               // version changes, so this long TTL is a safety cap.
  REFERENCE: 20 * 60 * 1000, // 20 min
  BUSINESS: 12 * 60 * 1000, // 12 min
  VOLATILE: 5 * 60 * 1000, // 5 min
} as const;

/** Stable cache-key builders. Convention: `admin:<domain>[:<variant>]`. */
export const cacheKeys = {
  players: 'admin:players',
  coaches: 'admin:coaches',
  sponsors: 'admin:sponsors',
  pressReleases: 'admin:pressReleases',
  latestNews: 'admin:latestNews',
  games: 'admin:games',
  merchandise: 'admin:merchandise',
  marketingContacts: 'admin:marketingContacts',
  revenueData: 'admin:revenueData',
  overviewCounts: 'admin:overviewCounts',
  // NOTE: there is deliberately NO shared `employees` key. The employees
  // collection is read by several pages with DIFFERENT projected shapes, so each
  // uses its own key (employeesRoster / staffingEmployees / timeEmployees). A
  // single shared key here previously caused a cross-page cache-poisoning crash.
  custom: (domain: string, variant?: string) => `admin:${domain}${variant ? `:${variant}` : ''}`,
};

/** Track keys we've written so `invalidatePrefix` can find them (the underlying
 * cacheService doesn't expose its key set). */
const knownKeys = new Set<string>();

/**
 * Read-through cache. Returns the cached value if fresh, else runs `fetcher`,
 * caches the result, and returns it. A thrown fetcher is NOT cached (it rejects).
 * Pass `force` to bypass the cache (e.g. an explicit Refresh button).
 */
export async function cachedRead<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = CACHE_TTL.BUSINESS,
  force = false
): Promise<T> {
  knownKeys.add(key);
  if (force) cacheService.delete(key);
  return cacheService.getOrSet<T>(key, fetcher, { ttl });
}

/** Drop one cache entry (call after a successful create/update/delete). */
export function invalidate(key: string): void {
  cacheService.delete(key);
}

/** Drop every cache entry whose key starts with `prefix`. */
export function invalidatePrefix(prefix: string): void {
  for (const k of Array.from(knownKeys)) {
    if (k.startsWith(prefix)) {
      cacheService.delete(k);
      knownKeys.delete(k);
    }
  }
}

/** Clear the whole admin cache (e.g. on sign-out). */
export function clearAdminCache(): void {
  for (const k of Array.from(knownKeys)) cacheService.delete(k);
  knownKeys.clear();
}
